import { createStyles, Stack, Table } from '@mantine/core';
import { singularize } from '@repo/common/utils';
import { Text } from '@repo/foundations';
import { size } from 'lodash-es';
import { useCalculateCost } from '../../api/hooks/workspace/useLimits';

interface IPricingDetailsProps {
	type: 'viewers' | 'editors' | 'integrations';
	nominalCurrentCount: number;
	nominalChange: number;
}

const useStyles = createStyles((theme) => ({
	wrapper: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.md,
		padding: theme.spacing.xl,
	},
}));

export function PricingDetails({
	type,
	nominalCurrentCount,
	nominalChange,
}: IPricingDetailsProps) {
	const { classes } = useStyles();

	const { brackets, interval } = useCalculateCost({
		type,
		nominalCurrentCount,
		nominalNewCount: nominalCurrentCount + nominalChange,
	});

	if (size(brackets) === 0) {
		return null;
	}

	return (
		<Stack className={classes.wrapper} spacing="xl">
			<Text size="sm">
				Pricing breakdown for{' '}
				{singularize(type === 'editors' ? 'Editor/Admin/Custom' : type)} seats
			</Text>
			<Table>
				<thead>
					<tr>
						<th>Bracket</th>
						<th>Current</th>
						<th>New</th>
						<th>$ Seat/{interval}</th>
						<th>$ Increase</th>
					</tr>
				</thead>
				<tbody>
					{brackets.map((bracket) => (
						<tr
							aria-label={`Pricing details for ${bracket.bracketStart} - ${bracket.bracketEnd} seats`}
							key={bracket.bracketStart?.toString()}
						>
							<td>
								<Text>
									{bracket.bracketStart}
									{bracket.bracketEnd === Infinity
										? '+'
										: `-${bracket.bracketEnd}`}{' '}
									seats
								</Text>
							</td>
							<td>
								<Text>{bracket.existingSeatCountInThisBracket}</Text>
							</td>
							<td>
								<Text>{bracket.newSeatCountInThisBracket}</Text>
							</td>
							<td>
								<Text>
									{bracket.costPerSeat === 0
										? 'Included'
										: `${(bracket.costPerSeat / 100).toFixed(2)}`}
								</Text>
							</td>
							<td>
								<Text>
									{bracket.increaseInCost === 0
										? ''
										: `+${(bracket.increaseInCost / 100).toFixed(2)}`}
								</Text>
							</td>
						</tr>
					))}
				</tbody>
			</Table>
		</Stack>
	);
}
